import type { ReactNode } from "react";

// Günüm — dependency-free chart primitives, drawn in the console `k-`/`gunum-` kit
// language. NO chart library: an SVG donut (segmented ring), a thin SVG sparkline,
// and CSS horizontal bars (funnel). Each renders a server rollup verbatim — no
// curve is ever fabricated. Bands are marked by SHAPE + TEXT, never colour alone.

export type ChartTone = "amber" | "blue" | "neutral" | "red" | "green";

/** The shape marker paired with each tone so a band is legible without colour. */
export const TONE_MARKER: Record<ChartTone, string> = {
  amber: "▲",
  blue: "●",
  neutral: "○",
  red: "◆",
  green: "●",
};

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  tone: ChartTone;
}

/**
 * A segmented donut — today's items split by band. Zero-total renders just the
 * calm track ring (an honest empty, never a fabricated slice). The centre carries
 * a value + caption; the accessible name is supplied by the caller.
 */
export function BandDonut({
  segments,
  centerValue,
  centerCaption,
  ariaLabel,
  valueTestId,
}: {
  segments: DonutSegment[];
  centerValue: ReactNode;
  centerCaption?: ReactNode;
  ariaLabel: string;
  valueTestId?: string;
}) {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  let acc = 0;
  return (
    <div className="gunum-donut">
      <svg viewBox="0 0 84 84" className="gunum-donut__svg" role="img" aria-label={ariaLabel}>
        <g transform="rotate(-90 42 42)">
          <circle className="gunum-donut__track" cx="42" cy="42" r={r} />
          {total > 0
            ? segments
                .filter((seg) => seg.value > 0)
                .map((seg) => {
                  const arc = (seg.value / total) * circ;
                  const dashoffset = -((acc / total) * circ);
                  acc += seg.value;
                  return (
                    <circle
                      key={seg.key}
                      className={`gunum-donut__arc gunum-donut__arc--${seg.tone}`}
                      cx="42"
                      cy="42"
                      r={r}
                      strokeDasharray={`${arc} ${circ - arc}`}
                      strokeDashoffset={dashoffset}
                    />
                  );
                })
            : null}
        </g>
      </svg>
      <div className="gunum-donut__center">
        <span className="gunum-donut__value" data-testid={valueTestId}>
          {centerValue}
        </span>
        {centerCaption != null ? <span className="gunum-donut__cap">{centerCaption}</span> : null}
      </div>
    </div>
  );
}

/**
 * A thin line/sparkline for a short daily series. Purely presentational — the
 * verbal summary lives in `ariaLabel` (the accessible name), so a screen reader
 * hears the trend in words, not a shape. Flat/empty series → a calm baseline.
 */
export function Sparkline({
  values,
  ariaLabel,
  testId,
}: {
  values: number[];
  ariaLabel: string;
  testId?: string;
}) {
  const w = 168;
  const h = 40;
  const pad = 4;
  const max = Math.max(1, ...values);
  const n = values.length;
  const points = values.map((v, i) => {
    const x = n <= 1 ? pad : pad + (i * (w - pad * 2)) / (n - 1);
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="gunum-spark"
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <polyline className="gunum-spark__base" points={`${pad},${h - pad} ${w - pad},${h - pad}`} />
      {points.length > 0 ? (
        <polyline className="gunum-spark__line" points={points.join(" ")} />
      ) : null}
    </svg>
  );
}

export interface FunnelRow {
  key: string;
  label: string;
  value: number;
  tone: ChartTone;
}

/**
 * A calm horizontal funnel/bar list — a marker (shape) + label track + fill +
 * tabular value per row. Zero rows still render (an honest zero track), so the
 * pipeline never hides a stage.
 */
export function Funnel({
  rows,
  ariaLabel,
  testId,
}: {
  rows: FunnelRow[];
  ariaLabel: string;
  testId?: string;
}) {
  const max = Math.max(1, ...rows.map((row) => row.value));
  return (
    <ul className="gunum-funnel" role="img" aria-label={ariaLabel} data-testid={testId}>
      {rows.map((row) => (
        <li key={row.key} className="gunum-funnel__row">
          <span className="gunum-funnel__label">
            <span className="gunum-marker" aria-hidden="true">
              {TONE_MARKER[row.tone]}
            </span>
            {row.label}
          </span>
          <span className="gunum-funnel__track">
            <span
              className={`gunum-funnel__fill gunum-funnel__fill--${row.tone}`}
              style={{ width: `${(row.value / max) * 100}%` }}
            />
          </span>
          <span className="gunum-funnel__value">{row.value}</span>
        </li>
      ))}
    </ul>
  );
}
